import { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function normalisePath(p) {
  if (!p) return "/";
  let raw = String(p);
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try { raw = new URL(raw).pathname; } catch (_e) { /* keep raw */ }
  }
  let out = raw.split("?")[0].split("#")[0];
  if (out.length > 1 && out.endsWith("/")) out = out.slice(0, -1);
  return out;
}

// Shared cache so this component and SEORedirectHandler don't double-fetch.
let _redirectsPromise = null;
function fetchRedirects() {
  if (!_redirectsPromise) {
    _redirectsPromise = axios
      .get(`${API}/seo-public/redirects`)
      .then((r) => Array.isArray(r.data) ? r.data : [])
      .catch(() => []);
  }
  return _redirectsPromise;
}

/**
 * <AliasCatchAll /> — rendered as the final `path="*"` route.
 *
 * When Speicher renames a slug via the SEO portal (adds a 301 from OLD to NEW), the OLD
 * URL redirects fine but the NEW URL doesn't have a React route → falls through to this
 * catch-all. Here we:
 *   1. Look up the redirect table for a rule where `to_path === current URL`.
 *   2. If found, extract the `from_path` (the ORIGINAL page that has real content).
 *   3. Render <Navigate to={fromPath} replace /> — React Router unmounts this component
 *      and renders the source route's component. Once mounted, we restore the URL bar
 *      back to the alias URL via window.history.replaceState so visitor sees the renamed
 *      URL while the source content displays underneath.
 *   4. If no matching redirect rule → render a proper 404.
 */
export default function AliasCatchAll({ componentMap = {} }) {
  const location = useLocation();
  const [state, setState] = useState({ status: "checking", target: null });

  useEffect(() => {
    const startingHere = normalisePath(location.pathname).toLowerCase();
    let cancelled = false;
    fetchRedirects().then((rules) => {
      if (cancelled) return;
      const active = (rules || []).filter(
        (rule) => rule && rule.is_active !== false && rule.from_path && rule.to_path,
      );
      // Walk chain backwards: current URL is a `to_path` — find rule pointing to it,
      // hop to that rule's `from_path`, repeat until the from_path exists in componentMap
      // (real React route) or we hit a loop / 5-hop cap. Handles Speicher's chained-fix
      // scenario: old-slug → typo-slug → correct-slug lands on correct-slug and we need
      // to resolve back through the chain to reach the original React component.
      let here = startingHere;
      const visited = new Set([here]);
      let resolvedTarget = null;
      for (let hop = 0; hop < 5; hop += 1) {
        const inbound = active.find((r) => normalisePath(r.to_path).toLowerCase() === here);
        if (!inbound) break;
        const fromPath = normalisePath(inbound.from_path).toLowerCase();
        if (visited.has(fromPath)) break; // loop protection
        visited.add(fromPath);
        // If this source is in the componentMap, we have a match — stop here.
        if (componentMap[fromPath]) {
          resolvedTarget = fromPath;
          break;
        }
        // Otherwise keep walking backwards through the chain
        resolvedTarget = fromPath; // best-effort fallback if chain terminates without a map hit
        here = fromPath;
      }
      if (resolvedTarget) {
        setState({ status: "resolved", target: resolvedTarget });
      } else {
        setState({ status: "notfound", target: null });
      }
    });
    return () => { cancelled = true; };
  }, [location.pathname, componentMap]);

  // Render the source page's component IN-PLACE — URL bar stays at the alias URL, so
  // all override hooks (usePageSEOOverride, usePageContentOverrides, useCanonical) see
  // the alias URL as `location.pathname` and apply Speicher's overrides configured for
  // that URL. This is the whole point: slug-renamed URL + fresh meta + fresh body = live.
  if (state.status === "resolved" && state.target) {
    const element = componentMap[state.target];
    if (element) return element;
    // Fallback if source path isn't in the map — do a normal Navigate so at least content shows
    return <Navigate to={state.target} replace />;
  }
  if (state.status === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 px-6">
        <div className="text-center max-w-md">
          <h1 className="text-4xl font-bold text-[#0F172A] mb-3">Page Not Found</h1>
          <p className="text-[#64748B] mb-6">The URL you followed doesn&apos;t exist. Head back to the home page or explore our products.</p>
          <div className="flex justify-center gap-3">
            <a href="/" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#EA7515] transition-colors">Home</a>
            <a href="/products" className="px-5 py-2.5 border border-[#334155] text-[#0F172A] text-sm font-semibold uppercase tracking-wide hover:border-[#F97316] hover:text-[#F97316] transition-colors">Products</a>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-[#94A3B8] text-sm">Loading&hellip;</div>
    </div>
  );
}
